import { styled } from '@mui/system';
import useMediaQuery from '@mui/material/useMediaQuery';
import Skeleton from '@mui/material/Skeleton';
import { Typography } from '@components/Typography';
import { useTranslation } from '@hooks';
import Percentage from './Percentage';
import { Trans } from 'react-i18next';
import { formatNumber } from '@helpers/formatNumbers';

const CardWrapper = styled('div', {
    shouldForwardProp: prop => prop !== 'withBorder',
})(({ withBorder }) => ({
    border: withBorder ? '1px solid var(--divider)' : 'none',
    cursor: withBorder ? 'pointer' : 'default',
    borderRadius: 8,
    padding: withBorder ? '14px 16px 16px' : '8px 12px',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    gap: withBorder ? 8 : 6,
    minHeight: withBorder ? 110 : 96,
    '&:hover': {
        backgroundColor: withBorder ? 'rgba(0, 0, 0, 0.02)' : 'transparent',
    },
    '@media (max-width: 1200px)': {
        minHeight: withBorder ? 102 : 88,
    },
    '@media (max-width: 820px)': {
        maxWidth: 'none',
        minHeight: withBorder ? 104 : 90,
    },
}));

const ValueWrapper = styled('div', {
    shouldForwardProp: prop => prop !== 'hasIcon',
})(({ hasIcon }) => ({
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 16,
    ...(hasIcon && { display: 'grid', gridTemplateColumns: '28px auto', gridGap: 16 }),
    '@media (max-width: 1200px)': {
        gap: 8,
        ...(hasIcon && { gridGap: 8 }),
    },
    '@media (max-width: 820px)': {
        '& svg': {
            width: 28,
            height: 28,
        },
        ...(hasIcon && {
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            gridTemplateColumns: 'none',
            gap: 8,
        }),
    },
}));

const MetricItem = ({
    title,
    value,
    subvalue,
    withPercent,
    withSign,
    withBorder,
    Icon = null,
    onClick = null,
    loading = false,
}) => {
    const { t } = useTranslation();
    const isSmallScreen = useMediaQuery('@media (max-width: 1200px)');
    const isSubvalueExists = subvalue ?? false;
    return (
        <CardWrapper withBorder={withBorder} onClick={onClick}>
            <Typography
                variant="caption"
                sx={{ display: 'inline-block', color: 'var(--text-secondary)', textAlign: 'center' }}
            >
                {title}
            </Typography>
            <ValueWrapper hasIcon={!!Icon}>
                {Icon}
                {loading ? (
                    <Skeleton
                        variant="text"
                        width={isSmallScreen ? 40 : 56}
                        sx={{ fontSize: isSmallScreen ? '2.125rem' : '3rem' }}
                    />
                ) : (
                    <Typography
                        variant={isSmallScreen ? 'h4' : 'h3'}
                        sx={{ wordBreak: 'break-word', textAlign: 'center', color: 'var(--primary-main)' }}
                    >
                        {value === null || value === undefined ? '—' : formatNumber(value, 'compact')}
                        {withPercent && value !== null && value !== undefined && '%'}
                    </Typography>
                )}
            </ValueWrapper>
            {isSubvalueExists !== false && !loading ? (
                <Typography
                    variant="caption"
                    sx={{ display: 'block', textAlign: 'center', gap: '4px', color: 'var(--text-secondary)' }}
                >
                    <Trans t={t} i18nKey="HomePage.vsPrevMonth">
                        <Percentage value={subvalue} withSign={withSign} />
                    </Trans>
                </Typography>
            ) : null}
        </CardWrapper>
    );
};

export default MetricItem;
